import { useCallback, useEffect, useRef, useState } from "react";
import {
	ENVIRONMENT_ROOT_VOLUME_GIB,
	MAX_ENVIRONMENTS_PER_DEVICE,
	createEnvironment,
	getEnvironmentRootVolumeOptions,
	listRunners,
	type Environment,
	type EnvironmentRootVolumeOptions,
	type Runner,
} from "@/lib/api";
import { formatRateCredits } from "@/lib/billing";
import { parseRootVolumeGib, rootVolumeCreditsPerHour } from "@/lib/environment-root-volume";
import { INPUT_STYLE, focusRingOn, focusRingOff } from "@/lib/form-styles";
import { canSeeRunnerManagement } from "@/lib/runner-access";
import { InputField } from "@/components/common/InputField";
import { SelectInputField } from "@/components/common/SelectInputField";
import { TextInputField } from "@/components/common/TextInputField";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";

const HINT_STYLE: React.CSSProperties = {
	margin: 0,
	fontSize: "var(--text-caption)",
	color: "var(--fg-2)",
	fontFamily: "var(--font-sans)",
};

/** 実行場所のセレクトの「クラウド」の値。デバイスは runner の id を値にする。 */
const CLOUD_LOCATION = "";

function isRunnerFull(runner: Runner): boolean {
	return runner.environmentCount >= MAX_ENVIRONMENTS_PER_DEVICE;
}

function runnerLocationLabel(runner: Runner): string {
	const notes = [
		...(runner.online ? [] : ["オフライン"]),
		...(isRunnerFull(runner)
			? [`エージェントが上限の ${MAX_ENVIRONMENTS_PER_DEVICE} つに達しています`]
			: []),
	];
	return `デバイス: ${runner.name}${notes.length > 0 ? ` (${notes.join("、")})` : ""}`;
}

interface LaunchEnvironmentDialogProps {
	open: boolean;
	/** 作成先のプロジェクト。 */
	projectId: string;
	onClose: () => void;
	onCreated: (environment: Environment) => void;
}

/**
 * 選択中プロジェクトに新しい環境を作成するモーダルダイアログ。環境名とルートボリューム容量 (GiB) を
 * 指定する。容量は作成後に変えられないため、選択肢の範囲と稼働 1 時間あたりの容量分クレジットを
 * サーバーの単価から見せて判断材料にする。範囲と既定はフロントの定数で開いた瞬間に描き、非同期で
 * 届く単価は高さを確保した 1 行に後から埋める (読み込みでレイアウトが動かないようにする)。設定 (許可ドメイン) は
 * 既定値で作成される (作成後にカードの「設定」から変更できる)。
 * デバイスで動かせる人 (組織の管理者) で、有効なデバイスが 1 つ以上あるときだけ「実行場所」を出す。オフラインのデバイスも
 * 選べる (起動はオンラインになってから)。作ったエージェントが上限 (MAX_ENVIRONMENTS_PER_DEVICE) に達したデバイスは、理由を添えて
 * 選べなくする。デバイスを選ぶとデバイスのディスクを使うので容量の欄を隠す。サイズと GPU を使うかは
 * 起動のときに選ぶ。デバイスの一覧の取得中は作成させず、取得に失敗したら再試行を出す (クラウドには作れる)。
 */
export function LaunchEnvironmentDialog({
	open,
	projectId,
	onClose,
	onCreated,
}: LaunchEnvironmentDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);

	const [name, setName] = useState("");
	const [rootVolumeInput, setRootVolumeInput] = useState(
		String(ENVIRONMENT_ROOT_VOLUME_GIB.default),
	);
	const [volumeOptions, setVolumeOptions] = useState<EnvironmentRootVolumeOptions | null>(null);
	const [volumeOptionsError, setVolumeOptionsError] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const role = useCurrentUserStore((s) => s.role);
	const deviceManagementAvailable = useCurrentUserStore((s) => s.deviceManagementAvailable);
	const canChooseRunner = canSeeRunnerManagement(role, deviceManagementAvailable);
	const [runners, setRunners] = useState<Runner[]>([]);
	const [runnersError, setRunnersError] = useState<string | null>(null);
	const [runnersLoading, setRunnersLoading] = useState(false);
	const [runnersAttempt, setRunnersAttempt] = useState(0);
	const [location, setLocation] = useState(CLOUD_LOCATION);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
		} else if (!open && el.open) {
			el.close();
		}
	}, [open]);

	useEffect(() => {
		if (!open) return;
		setName("");
		setRootVolumeInput(String(ENVIRONMENT_ROOT_VOLUME_GIB.default));
		setVolumeOptions(null);
		setVolumeOptionsError(null);
		setError(null);
		setSubmitError(null);
		setLoading(false);
		setLocation(CLOUD_LOCATION);
		setRunners([]);
		setRunnersError(null);
		const controller = new AbortController();
		getEnvironmentRootVolumeOptions(controller.signal)
			.then(setVolumeOptions)
			.catch((err: unknown) => {
				if (controller.signal.aborted) return;
				setVolumeOptionsError(
					err instanceof Error ? err.message : "ルートボリュームの選択肢の取得に失敗しました。",
				);
			});
		return () => controller.abort();
	}, [open]);

	useEffect(() => {
		if (!open || !canChooseRunner) return;
		let cancelled = false;
		setRunnersLoading(true);
		setRunnersError(null);
		const load = async () => {
			try {
				const list = await listRunners();
				if (!cancelled) setRunners(list.filter((runner) => runner.status === "active"));
			} catch (err) {
				if (!cancelled)
					setRunnersError(
						err instanceof Error ? err.message : "デバイスの一覧の取得に失敗しました。",
					);
				throw err;
			} finally {
				if (!cancelled) setRunnersLoading(false);
			}
		};
		void load().catch((err: unknown) => {
			console.error("デバイスの一覧の取得に失敗しました", err);
		});
		return () => {
			cancelled = true;
		};
	}, [open, canChooseRunner, runnersAttempt]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				e.preventDefault();
			}
		},
		[onClose],
	);

	const selectedRunner = runners.find((runner) => runner.id === location) ?? null;
	const rootVolume = parseRootVolumeGib(rootVolumeInput);
	// 一覧が届く前に作成すると、デバイスを選べないまま (課金される) クラウドに作ってしまうので待たせる。
	const waitingForRunners = canChooseRunner && runnersLoading;
	const canSubmit =
		!waitingForRunners &&
		name.trim().length > 0 &&
		(selectedRunner !== null || rootVolume.gib !== null);
	// 単価は判断材料であって作成の条件ではないので、届くまで (または失敗しても) 送信は妨げない。
	const rootVolumeRate =
		volumeOptions !== null && rootVolume.gib !== null
			? `容量の加算: ${formatRateCredits(rootVolumeCreditsPerHour(rootVolume.gib, volumeOptions.creditsPerGibHour))} クレジット / 時間 (停止中も同額)`
			: "";

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!name.trim()) {
			setError("エージェント名は必須です。");
			return;
		}
		if (selectedRunner === null && rootVolume.gib === null) return;
		setError(null);
		setSubmitError(null);
		setLoading(true);
		try {
			const created = await createEnvironment(
				selectedRunner
					? { name: name.trim(), projectId, runnerId: selectedRunner.id }
					: { name: name.trim(), projectId, rootVolumeGib: rootVolume.gib ?? undefined },
			);
			onCreated(created);
			onClose();
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			data-testid="launch-environment-dialog"
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-card)",
				background: "var(--surface-1)",
				width: 480,
			}}
			className="backdrop:bg-scrim"
		>
			<form onSubmit={handleSubmit} noValidate>
				<div style={{ padding: "20px 24px 16px", borderBottom: "var(--pane-header-border)" }}>
					<h3
						style={{
							margin: 0,
							fontSize: "var(--text-title)",
							fontWeight: 600,
							color: "var(--fg-1)",
							fontFamily: "var(--font-sans)",
						}}
					>
						エージェントを作成
					</h3>
				</div>
				<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
					<TextInputField
						label="エージェント名"
						value={name}
						onChange={setName}
						autoComplete="off"
						error={error ?? undefined}
					/>
					{waitingForRunners && (
						<p style={HINT_STYLE} data-testid="launch-environment-runners-loading">
							デバイスの一覧を取得しています…
						</p>
					)}
					{runnersError && (
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<p
								style={{ ...HINT_STYLE, color: "var(--danger)" }}
								data-testid="launch-environment-runners-error"
							>
								デバイスの一覧を取得できないため、クラウドに作成します: {runnersError}
							</p>
							<ConfirmButton
								label="再試行"
								loading={false}
								variant="secondary"
								size="sm"
								onClick={() => setRunnersAttempt((attempt) => attempt + 1)}
								data-testid="launch-environment-runners-retry"
							/>
						</div>
					)}
					{runners.length > 0 && (
						<SelectInputField
							label="実行場所"
							value={location}
							onChange={setLocation}
							testId="launch-environment-location"
						>
							<option value={CLOUD_LOCATION}>クラウド</option>
							{runners.map((runner) => (
								<option key={runner.id} value={runner.id} disabled={isRunnerFull(runner)}>
									{runnerLocationLabel(runner)}
								</option>
							))}
						</SelectInputField>
					)}
					{selectedRunner && (
						<p style={HINT_STYLE}>
							デバイスのディスクで動きます。サイズ (CPU・メモリ・GPU)
							はクラウドと同じく起動のときに選びます。
						</p>
					)}
					{selectedRunner === null && (
						<InputField
							label="ルートボリューム容量 (GiB)"
							error={rootVolume.error ?? volumeOptionsError ?? undefined}
						>
							<input
								type="number"
								inputMode="numeric"
								value={rootVolumeInput}
								min={ENVIRONMENT_ROOT_VOLUME_GIB.min}
								max={ENVIRONMENT_ROOT_VOLUME_GIB.max}
								step={1}
								onChange={(e) => setRootVolumeInput(e.target.value)}
								style={INPUT_STYLE}
								onFocus={(e) => focusRingOn(e.currentTarget)}
								onBlur={(e) => focusRingOff(e.currentTarget)}
								data-testid="launch-environment-root-volume-gib"
							/>
							<p style={HINT_STYLE}>
								{ENVIRONMENT_ROOT_VOLUME_GIB.min} 〜 {ENVIRONMENT_ROOT_VOLUME_GIB.max}{" "}
								GiB。作成後は拡張のみでき、縮小はできません。
							</p>
							{/* 単価が届く前から 1 行分の高さを確保し、後から埋まってもレイアウトが動かないようにする。 */}
							<p
								style={{ ...HINT_STYLE, minHeight: "1.5em", lineHeight: "var(--leading-normal)" }}
								data-testid="launch-environment-root-volume-estimate"
							>
								{rootVolumeRate}
							</p>
						</InputField>
					)}
					{submitError && (
						<div
							style={{
								padding: "10px 14px",
								borderRadius: "var(--radius-3)",
								background: "var(--danger-subtle)",
								boxShadow: "0 0 0 1px var(--danger-line)",
								fontSize: "var(--text-ui)",
								color: "var(--danger)",
								fontFamily: "var(--font-sans)",
							}}
						>
							{submitError}
						</div>
					)}
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						borderTop: "var(--pane-header-border)",
						padding: "12px 24px",
					}}
				>
					<ConfirmButton
						variant="ghost"
						label="キャンセル"
						loading={false}
						disabled={loading}
						onClick={onClose}
					/>
					<ConfirmButton
						type="submit"
						label="作成"
						loading={loading}
						disabled={!canSubmit}
						data-testid="launch-environment-submit"
					/>
				</div>
			</form>
		</dialog>
	);
}
